import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMe } from '../features/authSlice';
import { useStateContext } from "../contexts/ContextProvider";

import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaTimesCircle,
  FaLeaf,
  FaShoppingBag,
  FaBoxOpen,
} from "react-icons/fa";

// ── Helpers ─────────────────────────────────────────────────────────────────
const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value);

const truncateText = (text, max = 90) =>
  text.length <= max ? text : text.substring(0, max) + "…";

const ListProduk = () => {
  const [produk, setProduk] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduk, setSelectedProduk] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentColor, currentMode } = useStateContext();

  // ── Theme ────────────────────────────────────────────────────────────────
  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      getProduk();
    } else {
      navigate('/');
    }
  }, [navigate, user]);

  const getProduk = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/produk`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProduk(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // ── Filter ───────────────────────────────────────────────────────────────
  const filteredProduk = produk.filter((p) =>
    p.nama.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Delete handlers ──────────────────────────────────────────────────────
  const handleDeleteClick = (produk) => {
    setSelectedProduk(produk);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedProduk(null);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.delete(`${apiUrl}/produk/${selectedProduk.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      setSelectedProduk(null);
      getProduk();
    } catch (error) {
      console.error('Error deleting produk:', error);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background deco */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaShoppingBag
            className="absolute bottom-40 left-20 text-7xl"
            style={{ color: accentColor }}
          />
          <FaLeaf
            className="absolute top-60 right-1/4 text-5xl"
            style={{ color: accentColor }}
          />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div
            className="p-6 rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: accentColor }}>
                  <FaShoppingBag className="text-white text-2xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                      Produk Management
                    </span>
                    <span className="text-xs" style={{ color: textSecondary }}>—</span>
                    <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                      Rumoh Pangan Aceh
                    </span>
                  </div>
                  <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                    Data Produk
                  </h1>
                  <p className="text-sm mt-1" style={{ color: textSecondary }}>
                    Manajemen produk Rumoh Pangan Aceh
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/add-produk")}
                className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-300 hover:shadow-lg font-medium"
                style={{ backgroundColor: accentColor }}
              >
                <FaPlus className="text-sm" />
                Tambah Produk
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6"
        >
          <div
            className="p-4 rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="relative flex-1 w-full">
                <FaSearch
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-sm"
                  style={{ color: textSecondary }}
                />
                <input
                  type="text"
                  placeholder="Cari nama atau deskripsi produk..."
                  className="w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 outline-none"
                  style={{ backgroundColor: inputBg, borderColor: inputBorder, color: textPrimary }}
                  onFocus={(e) => (e.target.style.borderColor = accentColor)}
                  onBlur={(e) => (e.target.style.borderColor = inputBorder)}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* Count badge */}
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-lg border whitespace-nowrap"
                style={{ backgroundColor: `${accentColor}10`, borderColor: `${accentColor}30`, color: accentColor }}
              >
                <FaBoxOpen className="text-sm" />
                <span className="text-sm font-semibold">{filteredProduk.length} Produk</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Grid ── */}
        {filteredProduk.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filteredProduk.map((produk, index) => (
              <motion.div
                key={produk.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="rounded-xl border overflow-hidden flex flex-col group"
                style={{ backgroundColor: cardBg, borderColor }}
              >
                {/* Image */}
                <div className="relative overflow-hidden" style={{ height: "180px" }}>
                  <img
                    src={produk.url}
                    alt={produk.nama}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentElement.style.backgroundColor = `${accentColor}15`;
                    }}
                  />
                  {/* Overlay gradient */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(to top, ${isDark ? "rgba(26,26,24,0.7)" : "rgba(0,0,0,0.25)"} 0%, transparent 60%)`,
                    }}
                  />
                  {/* Unit badge */}
                  <div
                    className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: accentColor, color: "#fff" }}
                  >
                    {produk.unit} Gram
                  </div>
                  {/* Action buttons – appear on hover */}
                  <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                    <button
                      onClick={() => navigate(`/produk/edit/${produk.id}`)}
                      className="p-2 rounded-lg shadow-md backdrop-blur-sm transition-all duration-200 hover:scale-110"
                      style={{ backgroundColor: accentColor, color: "#fff" }}
                      title="Edit produk"
                    >
                      <FaEdit className="text-xs" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(produk)}
                      className="p-2 rounded-lg shadow-md backdrop-blur-sm transition-all duration-200 hover:scale-110"
                      style={{ backgroundColor: "#dc2626", color: "#fff" }}
                      title="Hapus produk"
                    >
                      <FaTrash className="text-xs" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 p-4">
                  <h3
                    className="font-serif font-bold text-sm leading-snug mb-2 line-clamp-2"
                    style={{ color: textPrimary }}
                  >
                    {produk.nama}
                  </h3>

                  {/* Divider accent */}
                  <div
                    className="w-8 h-0.5 mb-3 rounded-full"
                    style={{ backgroundColor: accentColor }}
                  />

                  <p
                    className="text-xs leading-relaxed flex-1 mb-4"
                    style={{ color: textSecondary }}
                  >
                    {truncateText(produk.deskripsi)}
                  </p>

                  {/* Price + actions row */}
                  <div
                    className="flex items-center justify-between pt-3 border-t"
                    style={{ borderColor }}
                  >
                    <div>
                      <p className="text-xs mb-0.5" style={{ color: textSecondary }}>
                        Harga
                      </p>
                      <p className="text-base font-bold font-serif" style={{ color: accentColor }}>
                        {formatRupiah(produk.harga)}
                      </p>
                    </div>
                    {/* Mobile action buttons (always visible) */}
                    <div className="flex gap-2 md:hidden">
                      <button
                        onClick={() => navigate(`/produk/edit/${produk.id}`)}
                        className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                        style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                      >
                        <FaEdit className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(produk)}
                        className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                        style={{ backgroundColor: "#fef2f2", color: "#dc2626" }}
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-24 text-center rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <FaBoxOpen
              className="text-6xl mb-4 mx-auto opacity-30"
              style={{ color: textSecondary }}
            />
            <h3 className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>
              Tidak ada produk ditemukan
            </h3>
            <p className="text-sm mb-6" style={{ color: textSecondary }}>
              {searchTerm ? "Coba ubah kata kunci pencarian" : "Tambahkan produk baru untuk memulai"}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate("/add-produk")}
                className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-300 font-medium"
                style={{ backgroundColor: accentColor }}
              >
                <FaPlus className="text-sm" />
                Tambah Produk
              </button>
            )}
          </motion.div>
        )}
      </div>

      {/* ── Delete Modal ── */}
      {showDeleteModal && selectedProduk && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleDeleteCancel}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden z-10"
            style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}
          >
            {/* Modal Header */}
            <div
              className="px-6 py-4"
              style={{ background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" }}
            >
              <div className="flex items-center gap-3">
                <FaTimesCircle className="h-6 w-6 text-white" />
                <h3 className="text-lg font-bold font-serif text-white">Hapus Produk</h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#fef2f2" }}>
                  <FaTrash className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm leading-relaxed" style={{ color: textPrimary }}>
                    Apakah Anda yakin ingin menghapus produk{" "}
                    <strong>{selectedProduk.nama}</strong> dari Rumoh Pangan Aceh? Tindakan ini tidak dapat dibatalkan.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="px-6 py-4 border-t flex gap-3 justify-end"
              style={{ backgroundColor: isDark ? "#1a1a18" : "#f7f3ec", borderColor }}
            >
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="px-5 py-2.5 border rounded-xl text-sm font-medium transition-all duration-300"
                style={{ borderColor, color: textPrimary, backgroundColor: cardBg }}
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all duration-300"
                style={{ backgroundColor: "#dc2626" }}
              >
                Hapus Produk
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ListProduk;